import React, { useState, useRef, useMemo } from 'react';
import { Storage } from '@capacitor/storage';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonPage,
  IonButtons,
  IonMenuButton,
  IonRow,
  IonCol,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonText,
  IonModal,
  IonIcon,
  IonAlert,
  IonSearchbar,
  IonSpinner
} from '@ionic/react';
import { logIn, openOutline, searchOutline } from 'ionicons/icons';
import './Login.scss';
import { configService } from '../services/ConfigService';
import { getAppConfig } from '../config/app.config';
import { loginWithEvent as loginWithEventService } from '../data/eventsModal/events.service';
import {
  setIsLoggedIn,
  setUsername,
  setUserCed,
  setUserID,
  setUserOther,
  setEventCode,
  setEventName,
  setEventStartDate,
  setEventEndDate,
  setEventLocation
} from '../data/user/user.actions';
import { connect } from '../data/connect';
import { RouteComponentProps } from 'react-router';
import { loadConfData } from '../data/sessions/sessions.actions';
import FavoritesService from '../services/FavoritesService';
import { getUserData } from '../data/dataApi';
import NetworkingService from '../services/NetworkingService';
import FirebaseTokenService from '../services/FirebaseTokenService';
import { isFeatureEnabled } from '../config/app.config';

interface OwnProps extends RouteComponentProps {}

interface DispatchProps {
  setIsLoggedIn: typeof setIsLoggedIn;
  setUsername: typeof setUsername;
  setUserCed: typeof setUserCed;
  setUserID: typeof setUserID;
  setUserOther: typeof setUserOther;
  setEventCode: typeof setEventCode;
  setEventName: typeof setEventName;
  setEventStartDate: typeof setEventStartDate;
  setEventEndDate: typeof setEventEndDate;
  setEventLocation: typeof setEventLocation;
  loadConfData: typeof loadConfData;
}

interface Evento {
  texto2: string;  // Nombre del evento
  texto3?: string; // Fecha y hora de inicio
  texto4?: string; // Fecha y hora de finalización
  texto6?: string; // Ubicación del evento
  eventCode: string;
}

interface EventoCert {
  texto2?: string;  // Nombre del evento
  texto3?: string; // Fecha y hora de inicio
  texto4?: string; // Fecha y hora de finalización
  texto6?: string; // Ubicación del evento
  eventCode: string;
}

interface LoginProps extends OwnProps, DispatchProps {}

const Login: React.FC<LoginProps> = ({
  setIsLoggedIn,
  history,
  setUsername: setUsernameAction,
  setUserCed: setUserCedAction,
  setUserID: setUserIDAction,
  setUserOther: setUserOtherAction,
  setEventCode: setEventCodeAction,
  setEventName: setEventNameAction,
  setEventStartDate: setEventStartDateAction,
  setEventEndDate: setEventEndDateAction,
  setEventLocation: setEventLocationAction,
  loadConfData: loadConfDataAction
}) => {

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);


  // Obtener configuración de la app
  const appConfig = getAppConfig();

  // Estados para el modal de eventos
  const [showModal, setShowModal] = useState(false);
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [eventosCert, setEventosCert] = useState<EventoCert[]>([]);
  const [eventoError, setEventoError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertHeader, setAlertHeader] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingEvent, setLoadingEvent] = useState<string | null>(null);
  const [loadingCertEvent, setLoadingCertEvent] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Función para hacer login directo con un evento específico
  const selectEventDirectly = async (evento: any, userced: string) => {
    console.log("Ejecutando login directo con evento:", evento);

    await loginWithEventService(
      evento,
      userced,
      setIsLoggedIn,
      setUsernameAction,
      setUserCedAction,
      setUserIDAction,
      setUserOtherAction,
      setEventCodeAction,
      setEventNameAction,
      setEventStartDateAction,
      setEventEndDateAction,
      setEventLocationAction,
      () => {}, // setShowEventsModal - no necesario aquí
      history,
      undefined, // setLoading - opcional
      undefined, // setEventoError - opcional
      loadConfDataAction // ✅ NUEVO: Pasar la acción para cargar datos de conferencia
    );
  };

  // Función de login inicial
  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    setIsLoggingIn(true);

    if (password) {
      try {
        // Usar ConfigService para login multi-evento
        const response = await configService.loginMultiEvent(password);

        if (!response.ok) {
          throw new Error(`Error de red: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log("Eventos recibidos:", data.eventos);

        if (data.error) {
          setAlertHeader("Error de autenticación");
          setAlertMessage("La identificación ingresada no es válida. Por favor, verifique sus datos e intente nuevamente.");
          setShowAlert(true);
          setIsLoggingIn(false);
        } else {
          // Verificar si multievent está habilitado
          const isMultievent = configService.isMultieventEnabled();

          if (!isMultievent) {
            // Login directo - no mostrar modal, cargar evento directamente
            console.log("Login directo - cargando evento automáticamente");

            // Verificar que el login fue exitoso y hay datos del usuario
            if (data.username) {
              // Guardar datos del usuario en localStorage
              await Storage.set({ key: 'userced', value: password });
              await Storage.set({ key: 'username', value: data.username });
              await Storage.set({ key: 'eventname', value: data.eventname || 'Evento' });
              await Storage.set({ key: 'eventcode', value: configService.getDefaultEventID() || '' });

              // ✅ IMPORTANTE: Actualizar el estado global de Redux
              await setIsLoggedIn(true);
              setUsernameAction(data.username);
              setUserCedAction(password);
              setEventCodeAction(configService.getDefaultEventID() || '');
              setEventNameAction(data.eventname || 'Evento');

              console.log("Usuario autenticado:", data.username);
              console.log("Evento:", data.eventname);
              console.log("Estado de login actualizado");

              // Redirigir directamente a la aplicación
              const defaultRoute = getAppConfig().auth.defaultRoute || '/tabs/schedule';
              history.push(defaultRoute, 'forward');
            } else {
              setAlertHeader("Error de autenticación");
              setAlertMessage("No se pudo completar el login. Intente nuevamente.");
              setShowAlert(true);
            }
          } else {
            // Multievent habilitado - verificar si hay evento por defecto para login directo
            console.log("Multievent habilitado - verificando evento por defecto");

            const defaultEventID = configService.getDefaultEventID();
            let shouldShowModal = true;

            // Verificamos la propiedad "eventos"
            if (data.eventos && Array.isArray(data.eventos) && data.eventos.length > 0) {
              setEventos(data.eventos);
              setEventoError("");

              // Si hay evento por defecto, buscar y hacer login directo
              if (defaultEventID) {
                console.log("Buscando evento por defecto para login directo:", defaultEventID);

                const defaultEvent = data.eventos.find((evento: any) =>
                  evento.eventCode === defaultEventID || evento.texto1 === defaultEventID
                );

                if (defaultEvent) {
                  console.log("Evento por defecto encontrado, ejecutando login directo sin modal:", defaultEvent);
                  shouldShowModal = false;

                  // Ejecutar login directo usando el servicio de eventos
                  setTimeout(async () => {
                    try {
                      await selectEventDirectly(defaultEvent, password);
                    } catch (error) {
                      console.error("Error en login directo:", error);
                      // Si falla, mostrar modal como fallback
                      setShowModal(true);
                    }
                  }, 100);
                }
              }
            } else {
              setEventos([]);
              setEventoError("No estás registrado en ningún evento");
            }

            // Verificamos si hay eventos certificados adicionales
            if (data.eventoscert && Array.isArray(data.eventoscert) && data.eventoscert.length > 0) {
              setEventosCert(data.eventoscert);

              // También verificar en eventos certificados si no se encontró en eventos normales
              if (defaultEventID && shouldShowModal) {
                console.log("Buscando evento por defecto en eventos certificados:", defaultEventID);

                const defaultCertEvent = data.eventoscert.find((evento: any) =>
                  evento.eventCode === defaultEventID || evento.texto1 === defaultEventID
                );

                if (defaultCertEvent) {
                  console.log("Evento por defecto encontrado en certificados, ejecutando login directo sin modal:", defaultCertEvent);
                  shouldShowModal = false;

                  setTimeout(async () => {
                    try {
                      await selectEventDirectly(defaultCertEvent, password);
                    } catch (error) {
                      console.error("Error en login directo con evento certificado:", error);
                      setShowModal(true);
                    }
                  }, 100);
                }
              }
            } else {
              setEventosCert([]);
            }

            // Solo mostrar modal si no se encontró evento por defecto
            if (shouldShowModal) {
              console.log("Mostrando modal de selección de eventos");
              setShowModal(true);
              setIsLoggingIn(false);
            } else {
              // Si no se muestra modal, significa que se redirigió automáticamente
              setIsLoggingIn(false);
            }
          }
        }
      } catch (error) {
        console.error("Error durante el inicio de sesión:", error);

        // Verificar si es un error 503
        if (error instanceof Error && error.message.includes('503')) {
          setAlertHeader("Error de servidor");
          setAlertMessage("El servidor de desarrollo no está disponible en este momento. Por favor, contacte al administrador del sistema.");
          setShowAlert(true);
        } else {
          setAlertHeader("Error de conexión");
          setAlertMessage("No se pudo conectar con el servidor. Por favor, verifique su conexión a internet e intente nuevamente.");
          setShowAlert(true);
        }
        setIsLoggingIn(false);
      }
    } else {
      setIsLoggingIn(false);
    }
  };

  // Filtramos los eventos según el término de búsqueda
  const filteredEventos = useMemo(() => {
    if (!searchTerm.trim()) return eventos;

    const searchTermLower = searchTerm.toLowerCase().trim();
    return eventos.filter(evento => {
      // Buscamos en el nombre del evento
      const nameMatch = evento.texto2?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de inicio
      const startDateMatch = evento.texto3?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de finalización
      const endDateMatch = evento.texto4?.toLowerCase().includes(searchTermLower);
      // Buscamos en la ubicación
      const locationMatch = evento.texto6?.toLowerCase().includes(searchTermLower);

      return nameMatch || startDateMatch || endDateMatch || locationMatch;
    });
  }, [eventos, searchTerm]);

  // Filtramos los eventos certificados según el término de búsqueda
  const filteredEventosCert = useMemo(() => {
    if (!searchTerm.trim()) return eventosCert;

    const searchTermLower = searchTerm.toLowerCase().trim();
    return eventosCert.filter(eventoCert => {
      // Buscamos en el nombre del evento
      const nameMatch = (eventoCert.texto2 || `Evento ${eventoCert.eventCode}`)?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de inicio
      const startDateMatch = eventoCert.texto3?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de finalización
      const endDateMatch = eventoCert.texto4?.toLowerCase().includes(searchTermLower);
      // Buscamos en la ubicación
      const locationMatch = eventoCert.texto6?.toLowerCase().includes(searchTermLower);

      return nameMatch || startDateMatch || endDateMatch || locationMatch;
    });
  }, [eventosCert, searchTerm]);

  // Función para hacer login nuevamente con el evento seleccionado
  const loginWithEvent = async (evento: Evento) => {
    // Establecemos el evento que está cargando
    setLoadingEvent(evento.texto2);

    try {
      // Usar ConfigService para login con evento específico
      const response = await configService.loginSingleEvent(password, evento.eventCode);

      if (!response.ok) {
        throw new Error(`Error de red: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Login con evento:", data);

      if (data.error) {
        setAlertHeader("Error de autenticación");
        setAlertMessage("Ocurrió un error al iniciar sesión con el evento seleccionado. Por favor, intente nuevamente.");
        setShowAlert(true);
      } else {
        // Actualizamos el store y redirigimos a la pantalla principal
        await setIsLoggedIn(true);
        setUsernameAction(data.username);
        setUserCedAction(data.ced);
        setUserIDAction(data.id);
        setUserOtherAction(data.company.concat("&&&", data.role));

        // ✅ CORREGIDO: Esperar a que se guarde el eventcode en Storage antes de cargar datos
        await setEventCodeAction(data.eventcode);

        console.log(data);
        // Guardamos la información del evento
        setEventNameAction(data.texto2 || evento.texto2 || '');
        setEventStartDateAction(data.texto3 || '');
        setEventEndDateAction(data.texto4 || '');
        setEventLocationAction(data.texto6 || '');

        // ✅ CORREGIDO: Recargar datos de la conferencia DESPUÉS de guardar eventcode
        console.log('🔄 Login: Ejecutando loadConfDataAction...');
        loadConfDataAction();
        console.log('✅ Login: loadConfDataAction ejecutado');

        // IMPORTANTE: Primero sincronizar favoritos desde Firebase, LUEGO inicializar servicio
        console.log('Login: Sincronizando favoritos desde Firebase...');
        try {
          // Llamar directamente a getUserData para sincronizar favoritos
          await getUserData();
          console.log('Login: Favoritos sincronizados desde Firebase');
        } catch (error) {
          console.warn('Login: Error al sincronizar favoritos:', error);
        }

        console.log('Login: Inicializando servicio de favoritos después de sincronización...');
        // Inicializar servicio de favoritos en tiempo real DESPUÉS de la sincronización
        try {
          const favoritesService = FavoritesService.getInstance();
          await favoritesService.initialize(data.id);

          // La sincronización forzada se maneja automáticamente por el servicio

          console.log('Login: FavoritesService inicializado y sincronizado correctamente');
        } catch (error) {
          console.warn('Login: Error al inicializar FavoritesService:', error);
        }

        // Inicializar perfil de networking si está habilitado
        console.log('Login: Verificando si networking está habilitado:', isFeatureEnabled('networking'));
        if (isFeatureEnabled('networking')) {
          try {
            console.log('Login: Inicializando perfil de networking...');
            console.log('Login: Datos completos del participante:', JSON.stringify(data, null, 2));

            // Primero autenticar con Firebase usando endpoint simplificado
            console.log('Login: Autenticando con Firebase...');
            const firebaseTokenService = FirebaseTokenService.getInstance();
            const tokenResult = await firebaseTokenService.getFirebaseToken(data);
            console.log('Login: Resultado de autenticación Firebase:', tokenResult);
            console.log('Login: Autenticación Firebase completada');

            const networkingService = NetworkingService.getInstance();

            // Mapear campos correctos de la tabla de participantes
            const fullName = `${data.texto1 || ''} ${data.texto2 || ''}`.trim();

            // Log detallado de los campos que estamos usando
            console.log('Login: Campos de networking extraídos:', {
              texto1: data.texto1,
              texto2: data.texto2,
              texto3: data.texto3,
              texto8: data.texto8,
              texto9: data.texto9,
              fullName: fullName,
              username: data.username,
              company: data.company
            });

            await networkingService.initializeProfile({
              id: data.id,
              name: fullName || data.username || '', // texto1 + texto2 (nombres y apellidos) o username como fallback
              email: data.texto3 || data.email || '', // texto3 es el email
              phone: data.texto9 || data.phone || '', // texto9 es el teléfono
              company: data.texto8 || data.company || '', // texto8 es la empresa
              position: data.position || '', // Si hay campo de cargo en el futuro
              eventCode: data.eventcode
            });

            console.log('Login: Perfil de networking inicializado exitosamente');
          } catch (error) {
            console.error('Login: Error al inicializar perfil de networking:', error);
            if (error instanceof Error) {
              console.error('Login: Stack trace:', error.stack);
            }
            // No interrumpir el flujo de login por errores de networking
          }
        } else {
          console.log('Login: Networking no está habilitado, saltando inicialización');
        }

        setShowModal(false);
        const defaultRoute = getAppConfig().auth.defaultRoute || '/tabs/schedule';
        history.push(defaultRoute, { direction: 'none' });
      }
    } catch (error) {
      console.error("Error durante el inicio de sesión con evento:", error);

      // Verificar si es un error 503
      if (error instanceof Error && error.message.includes('503')) {
        setAlertHeader("Error de servidor");
        setAlertMessage("El servidor de desarrollo no está disponible en este momento. Por favor, contacte al administrador del sistema.");
        setShowAlert(true);
      } else {
        setAlertHeader("Error de conexión");
        setAlertMessage("No se pudo conectar con el servidor. Por favor, verifique su conexión a internet e intente nuevamente.");
        setShowAlert(true);
      }
    } finally {
      // Limpiamos el estado de carga del evento
      setLoadingEvent(null);
    }
  };

  // Función para abrir eventos certificados
  const openCertEvent = (eventoCert: EventoCert) => {
    // Establecemos el evento que está cargando
    setLoadingCertEvent(eventoCert.texto2 || `Evento ${eventoCert.eventCode}`);

    // Usamos setTimeout para dar tiempo a que se muestre el indicador de carga
    setTimeout(() => {
      window.open(`https://www.esimposio.com/e/${eventoCert.eventCode}/${password}`, '_blank');
      // Limpiamos el estado de carga
      setLoadingCertEvent(null);
    }, 500);
  };

  return (
    <IonPage id="login-page">
      <IonContent className="login-content">
        <div className="login-container">
          {/* Left side with logo and welcome text */}
          <div className="login-left-panel" style={{ background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(5px)' }}>
            <div className="login-logo ion-text-center">
              <img src="assets/icon/favicon.png" alt="IESTEC LOGO" />
              <h2 style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Bienvenido</h2>
              <p style={{ color: 'var(--neutral-700)' }}>Por favor ingrese su identificación.</p>
            </div>
          </div>

          {/* Right side with login form */}
          <div className="login-right-panel" style={{ background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: '12px', boxShadow: 'var(--shadow-md)' }}>
            <h2 className="login-title" style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Iniciar Sesión</h2>

            <form noValidate onSubmit={login} className="login-form">
              <IonItem lines="none" className="login-input" style={{ '--background': 'rgba(255, 255, 255, 0.5)', '--color': 'var(--neutral-900)', borderRadius: '8px', border: '1px solid var(--neutral-300)' }}>
                <IonLabel position="floating" style={{ color: 'var(--neutral-700)' }}>Identificación</IonLabel>
                <IonInput
                  name="password"
                  type={appConfig.auth.showPasswordAsText ? "text" : "password"}
                  value={password}
                  onIonChange={e => setPassword(e.detail.value!)}
                />
              </IonItem>

              {formSubmitted && passwordError && (
                <IonText color="danger">
                  <p className="ion-padding-start">Password is required</p>
                </IonText>
              )}

              <IonButton
                type="submit"
                expand="block"
                className="ion-margin-top"
                disabled={isLoggingIn}
                style={{ '--background': 'var(--primary-dark)', '--color': 'white', '--border-radius': '8px', fontWeight: '600', height: '44px' }}
              >
                {isLoggingIn ? (
                  <>
                    <IonSpinner name="crescent" slot="start" style={{ marginRight: '8px' }} />
                    Iniciando...
                  </>
                ) : (
                  <>
                    <IonIcon slot="start" icon={logIn}></IonIcon>
                    Entrar
                  </>
                )}
              </IonButton>
            </form>
          </div>
        </div>

        {/* Modal para mostrar el listado de eventos o mensaje de error */}
        <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)} className="events-modal">
          <IonHeader>
            <IonToolbar className="modal-toolbar">
              <IonTitle>Mis Eventos</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowModal(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
            {!eventoError && (eventos.length > 0 || eventosCert.length > 0) && (
              <IonToolbar>
                <IonSearchbar
                  value={searchTerm}
                  onIonChange={(e) => setSearchTerm(e.detail.value || '')}
                  placeholder="Buscar por nombre, fecha o ubicación..."
                  animated={true}
                  debounce={300}
                  showCancelButton="never"
                  style={{
                    '--background': document.body.classList.contains('dark-theme') ? '#1e1e1e' : 'rgba(255, 255, 255, 0.8)',
                    '--color': document.body.classList.contains('dark-theme') ? 'white' : 'black',
                    '--placeholder-color': document.body.classList.contains('dark-theme') ? 'rgba(255, 255, 255, 0.6)' : undefined,
                    'border': document.body.classList.contains('dark-theme') ? '1px solid rgba(255, 255, 255, 0.1)' : undefined,
                    'borderRadius': '8px'
                  }}
                />
              </IonToolbar>
            )}
          </IonHeader>
          <IonContent className="modal-content">
            {eventoError ? (
              <div className="error-message ion-padding ion-text-center ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid #ffcdd2', boxShadow: 'var(--shadow-sm)' }}>
                <IonText color="danger">
                  <p>{eventoError}</p>
                </IonText>
              </div>
            ) : searchTerm.trim() !== '' && filteredEventos.length === 0 && filteredEventosCert.length === 0 ? (
              <div className="ion-padding ion-text-center ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-sm)' }}>
                <IonText>
                  <p>No se encontraron eventos que coincidan con la búsqueda "{searchTerm}".</p>
                </IonText>
              </div>
            ) : (
              <>
                {filteredEventos.length > 0 && (
                  <div className="events-container ion-padding ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-sm)' }}>
                    <h2 className="ion-text-center" style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Actuales actividades académicas:</h2>
                    <IonList className="events-list">
                      {filteredEventos.map((evento, index) => (
                        <IonItem
                          key={index}
                          button
                          onClick={() => loadingEvent === null && loginWithEvent(evento)}
                          lines="none"
                          className="event-item"
                          style={{
                            '--background': 'rgba(255, 255, 255, 0.5)',
                            '--color': 'var(--neutral-900)',
                            borderRadius: '8px',
                            border: '1px solid var(--neutral-300)',
                            margin: '8px 0',
                            position: 'relative'
                          }}
                        >
                          {loadingEvent === evento.texto2 ? (
                            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                <IonSpinner name="crescent" style={{ color: 'var(--ion-color-primary)' }} />
                                <p style={{ margin: 0, color: 'var(--ion-color-primary)', fontWeight: 'bold' }}>Entrando...</p>
                              </div>
                            </div>
                          ) : (
                            <IonLabel>
                              <h2 style={{ fontWeight: 'bold', fontSize: '16px', whiteSpace: 'normal', overflow: 'visible' }}>
                                {evento.texto2}
                              </h2>
                              {(evento.texto3 || evento.texto4) && (
                                <p style={{ color: 'var(--neutral-700)', fontSize: '14px', marginTop: '4px' }}>
                                  <span style={{ display: 'block' }}>
                                    <strong>Inicio:</strong> {evento.texto3 || 'No disponible'}
                                  </span>
                                  <span style={{ display: 'block' }}>
                                    <strong>Fin:</strong> {evento.texto4 || 'No disponible'}
                                  </span>
                                  {evento.texto6 && (
                                    <span style={{ display: 'block' }}>
                                      <strong>Ubicación:</strong> {evento.texto6}
                                    </span>
                                  )}
                                </p>
                              )}
                            </IonLabel>
                          )}
                        </IonItem>
                      ))}
                    </IonList>
                  </div>
                )}
              </>
            )}

            {/* Sección de otros eventos (eventoscert) */}
            {eventosCert.length > 0 && filteredEventosCert.length > 0 && (
              <div className="other-events-container ion-padding ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-sm)', marginTop: '20px' }}>
                <h2 className="ion-text-center" style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Anteriores eventos:</h2>
                <IonList className="events-list">
                  {filteredEventosCert.map((eventoCert, index) => (
                    <IonItem
                      key={`cert-${index}`}
                      button
                      onClick={() => loadingCertEvent === null && openCertEvent(eventoCert)}
                      lines="none"
                      className="event-item"
                      style={{
                        '--background': 'rgba(255, 255, 255, 0.5)',
                        '--color': 'var(--ion-color-primary)',
                        borderRadius: '8px',
                        border: '1px solid var(--neutral-300)',
                        margin: '8px 0',
                        position: 'relative'
                      }}
                    >
                      {loadingCertEvent === (eventoCert.texto2 || `Evento ${eventoCert.eventCode}`) ? (
                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                            <IonSpinner name="crescent" style={{ color: 'var(--ion-color-primary)' }} />
                            <p style={{ margin: 0, color: 'var(--ion-color-primary)', fontWeight: 'bold' }}>Entrando...</p>
                          </div>
                        </div>
                      ) : (
                        <IonLabel>
                          <h2 style={{ fontWeight: 'bold', fontSize: '16px', whiteSpace: 'normal', overflow: 'visible' }}>
                            {eventoCert.texto2 || `Evento ${eventoCert.eventCode}`}
                          </h2>
                          {(eventoCert.texto3 || eventoCert.texto4) && (
                            <p style={{ color: 'var(--neutral-700)', fontSize: '14px', marginTop: '4px' }}>
                              <span style={{ display: 'block' }}>
                                <strong>Inicio:</strong> {eventoCert.texto3 || 'No disponible'}
                              </span>
                              <span style={{ display: 'block' }}>
                                <strong>Fin:</strong> {eventoCert.texto4 || 'No disponible'}
                              </span>
                              {eventoCert.texto6 && (
                                <span style={{ display: 'block' }}>
                                  <strong>Ubicación:</strong> {eventoCert.texto6}
                                </span>
                              )}
                            </p>
                          )}
                        </IonLabel>
                      )}
                      {!loadingCertEvent && <IonIcon icon={openOutline} slot="end" />}
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Alerta para mostrar mensajes de error */}
        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={alertHeader}
          message={alertMessage}
          buttons={['OK']}
          cssClass="error-alert"
        />
      </IonContent>
    </IonPage>
  );
};

export default connect<OwnProps, {}, DispatchProps>({
  mapDispatchToProps: {
    setIsLoggedIn,
    setUsername,
    setUserCed,
    setUserID,
    setUserOther,
    setEventCode,
    setEventName,
    setEventStartDate,
    setEventEndDate,
    setEventLocation,
    loadConfData
  },
  component: Login
});
